/**
 * The application frame: sidebar, top bar, page area and the context rail.
 *
 * The rail is 320px on a wide screen and becomes a drawer below 1280px, because the ERP is a desktop tool
 * and pretending otherwise would cost density where density is the point (DESIGN_SYSTEM §3).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { MENU_LABEL, NAV_SCREENS, RECORD_HOME, workspaceOf } from '@rtcom/domain';
import { publishRecordTitle } from '../ui/recordTitle';
import { screenAt } from './Sidebar';
import { useEmbedded } from '../routes/Hub';
import { ToastHost } from '../ui/toast';
import { useApplySavedAppearance, usePreferences, useSavePreferences } from '../lib/preferences';
import { ShortcutsHelp, WorkspaceTour } from '../ui/help';
import { useBusinessDay } from '../lib/businessDay';
import { cx } from '../ui/primitives';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { CommandPalette, rememberScreen, useCommandPalette } from './CommandPalette';
import { PlantAppLauncher } from '../features/plant/PlantAppLauncher';
import { AppearanceDialog, isDark, useAppearance } from './appearance';
import { ScrapFilters } from '../ui/ScrapNote';

/** The width class of the window: a phone (< 768 px), a medium screen (< 1280 px) or a wide one (D19 phase 5). */
function useWidth(): 'phone' | 'medium' | 'wide' {
  const read = () => (typeof window === 'undefined' ? 'wide' : window.innerWidth < 768 ? 'phone' : window.innerWidth < 1280 ? 'medium' : 'wide');
  const [w, setW] = useState<'phone' | 'medium' | 'wide'>(read);
  useEffect(() => {
    const on = () => setW(read());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return w;
}

export function AppShell() {
  const width = useWidth();
  // On a medium screen the sidebar starts as an icon rail; the person's own choice wins until the width changes.
  const [collapsedChoice, setCollapsedChoice] = useState<boolean | null>(null);
  useEffect(() => setCollapsedChoice(null), [width]);
  const collapsed = collapsedChoice ?? width === 'medium';
  // On a phone the sidebar is a drawer, opened from the top bar and closed when a screen is chosen.
  const [navOpen, setNavOpen] = useState(false);
  const [appearance, setAppearance] = useAppearance();
  // The person's own look, kept on the server; Classic until they choose another (Q0.8).
  useApplySavedAppearance();
  const savePreferences = useSavePreferences();
  // Each screen opened is remembered for the command palette's "recent" (D19 phase 8): the longest matching path wins.
  const { pathname } = useLocation();
  useEffect(() => {
    const screen = NAV_SCREENS.filter((s) => !s.hidden && (pathname === s.path || pathname.startsWith(`${s.path}/`))).sort((a, b) => b.path.length - a.path.length)[0];
    if (screen) rememberScreen(screen.id);
    // The workspace names the canvas's picture (appearance.css, [data-workspace]).
    document.documentElement.dataset.workspace = workspaceOf(screen?.id ?? '')?.id ?? 'home';
    setNavOpen(false);
  }, [pathname]);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const palette = useCommandPalette();

  // .shell, .shell-chrome and .shell-well are the glass style's layers (appearance.css); in the classic style they
  // add nothing, so the approved look is unchanged.
  return (
    <div className="shell flex h-screen overflow-hidden bg-paper">
      <a href="#main-content" className="skip-link">Skip to the page</a>
      {width !== 'phone' && <Sidebar collapsed={collapsed} onToggle={() => setCollapsedChoice(!collapsed)} />}
      {width === 'phone' && navOpen && (
        <div className="overlay-scrim fixed inset-0 z-50 flex bg-ink/25" role="presentation" onMouseDown={() => setNavOpen(false)}>
          <div className="shell-drawer h-full w-[280px] max-w-[85vw] shadow-[var(--shadow-overlay)]" onMouseDown={(e) => e.stopPropagation()}>
            <Sidebar collapsed={false} onToggle={() => setNavOpen(false)} />
          </div>
        </div>)}
      <div className="shell-chrome flex min-w-0 flex-1 flex-col">
        <TestOrganisationStrip />
        <TopBar onOpenPalette={() => palette.setOpen(true)} theme={isDark(appearance) ? 'dark' : 'light'} onToggleTheme={() => setAppearanceOpen(true)}
          onOpenNav={width === 'phone' ? () => setNavOpen(true) : undefined} />
        <main id="main-content" tabIndex={-1} className="shell-well min-h-0 flex-1 overflow-auto focus:outline-none">
          <Outlet />
        </main>
      </div>
      <ScrapFilters />
      <ToastHost />
      <ShortcutsHelp />
      <FirstVisitTour />
      <AppearanceDialog open={appearanceOpen} onClose={() => setAppearanceOpen(false)} value={appearance} onChange={(next) => { setAppearance(next); void savePreferences({ appearance: { ...appearance, ...next } }).catch(() => undefined); }} />
      <CommandPalette open={palette.open} onClose={() => palette.setOpen(false)} />
      <PlantAppLauncher />
    </div>
  );
}

/** The three-step tour on a person's first visit to a workspace, remembered on the server (Part D pattern 20). */
function FirstVisitTour() {
  const { pathname } = useLocation();
  const prefs = usePreferences();
  const save = useSavePreferences();
  const ws = workspaceOf(screenAt(pathname)?.id ?? '')?.id;
  if (!ws || !prefs.data) return null;
  const seen = prefs.data.toursSeen ?? [];
  return <WorkspaceTour key={ws} workspace={ws} seen={seen.includes(ws)} onDone={() => { void save({ toursSeen: [...seen, ws] }).catch(() => undefined); }} />;
}

/** A slim strip across the top, only in a TEST organisation (v7 CORE-1 §3): walk-throughs and test data live there. */
function TestOrganisationStrip() {
  const day = useBusinessDay();
  if (day.data?.environment !== 'TEST') return null;
  return <div role="note" className="bg-queued-ground px-4 py-0.5 text-center text-badge font-semibold text-queued">Test organisation · for walk-throughs and test data</div>;
}

/** The farm's and the plant's own marks (the owner's branding, 2026-10-01), shown beside a page title in their section. */
function SectionMark() {
  const { pathname } = useLocation();
  const mark = /^\/(farm|operations)(\/|$)/.test(pathname) ? { src: '/brand/rtcom-farm-logo.png', alt: 'RTCOM Farm' }
    : /^\/plant(\/|$)/.test(pathname) ? { src: '/brand/rtcom-plant-logo.png', alt: 'RTCOM Plant' } : null;
  return mark ? <img src={mark.src} alt={mark.alt} className="section-mark hidden h-10 w-auto shrink-0" /> : null;
}

/** The standard page frame: a title, an optional action area, and a footer stating scope and basis. */
export function Page({
  title, subtitle, actions, footer, children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** Scope, as-of and data origin. A page that shows figures should always say what they cover. */
  footer?: ReactNode;
  children: ReactNode;
}) {
  // v7 (CORE-1): inside a hub the hub already says what the page is, so the screen's own heading is dropped; at the top
  // level a screen v7 renames takes its Part E name, so the menu, the breadcrumb and the title say the same words.
  const embedded = useEmbedded();
  const { pathname } = useLocation();
  const routeScreen = screenAt(pathname);
  const shown = !embedded && routeScreen && MENU_LABEL[routeScreen.id] ? MENU_LABEL[routeScreen.id] : title;
  // The browser tab and a screen reader's page list say which screen this is (WCAG 2.4.2).
  useEffect(() => {
    if (!embedded && typeof shown === 'string') document.title = `${shown} · RTCOM ERP`;
    // A record page names its record in the breadcrumb (Workspace / List / Record).
    if (!embedded && routeScreen && RECORD_HOME[routeScreen.id]) publishRecordTitle(pathname, typeof shown === 'string' ? shown : null);
  }, [shown, embedded, pathname, routeScreen]);
  return (
    <div className="page-enter flex min-h-full flex-col">
      <div className={cx('flex items-start justify-between gap-6 px-6 pb-4', embedded ? 'pt-3' : 'pt-6')}>
        <div className="min-w-0">
          {embedded ? null : (
          <div className="flex items-center gap-3">
            <SectionMark />
            <h1 className="text-title">{shown}</h1>
          </div>)}
          {subtitle ? <p className="mt-1 max-w-prose text-body text-ink-muted">{subtitle}</p> : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>

      <div className="min-h-0 flex-1 px-6 pb-6">{children}</div>

      {footer ? (
        <footer className="border-t border-line px-6 py-2 text-small text-ink-muted">{footer}</footer>
      ) : null}
    </div>
  );
}
